import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paper, Box } from "@mui/material";
import "./modal.scss";
import {
  createDragResizeHandlers,
  repositionWithinBounds,
  parseSize,
} from "./drag-resize";
import TitleBar from "./title-bar";

export type tResizeCallback = {
  height: number;
  width: number;
};
export type tDragCallback = {
  left: number;
  top: number;
};

/**
 * @param scrollContent - Should the content (not the title bar) be scrollable
 */
export interface ModalProps {
  id: string;
  panelId?: string;
  initialPosition?: {
    left: number | string;
    top: number | string;
  };
  initialSize?: {
    height: number | string;
    width: number | string;
  };
  backgroundColor?: string;
  dragCallback?: (position: tDragCallback) => void;
  resizeCallback?: (size: tResizeCallback, id?: string) => void;
  resizable?: boolean;
  children?: React.ReactElement | React.ReactElement[];
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- preserve existing permissive style-object type; narrowing risks behavior change
  style?: {};
  scrollContent?: boolean;
  header?: string | React.ReactNode;
  footer?: React.ReactNode;
  /**
   * When true, the modal becomes modal-only: an overlay covers the viewport
   * and the modal traps focus/clicks. Mouse wheel still scrolls the page.
   */
  modalOnly?: boolean;
  bodyClassNames?: string[];
  onClose?: (props?: unknown) => void;
  /** Hide the title bar's built-in close button (caller renders its own header
   *  actions instead). */
  hideClose?: boolean;
  // titleBar removed — use `header` to provide customized header/content
}

const Modal: React.FC<ModalProps> = ({
  onClose,
  panelId,
  backgroundColor,
  initialPosition,
  initialSize,
  resizable,
  resizeCallback,
  dragCallback,
  children,
  style,
  scrollContent = true,
  header,
  footer,
  modalOnly = false,
  bodyClassNames,
  hideClose = false,
  // titleBar removed — header should contain TitleBar if needed
}) => {
  const mousePos = useRef({ x: 0, y: 0 });
  const componentSize = useRef({ height: 0, width: 0 });
  const componentPos = useRef({ left: 0, top: 0 });
  const elementRef = useRef<HTMLDivElement>(null);
  const selectedHandle = useRef<string>();
  const [zIndex] = useState<number>(1000);
  const [focusZIndex] = useState<number>();
  const [forceBodyScroll, setForceBodyScroll] = useState<boolean>(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const prevBodyOverflow = useRef<string | null>(null);

  const handleViewableAreaResize = useCallback(() => {
    // reposition window if needed after parent container is resized, and then update the window manager
    repositionWithinBounds(elementRef);
    if (dragCallback) {
      dragCallback({
        left: elementRef.current?.getBoundingClientRect().left ?? 100,
        top: elementRef.current?.getBoundingClientRect().top ?? 100,
      });
    }
  }, [dragCallback]);

  // Set a CSS variable used to compute the scrollable body's max-height.
  const setModalBodyOffset = useCallback(() => {
    const el = elementRef.current;
    if (!el) return;
    const headerEl = el.querySelector(
      ".workspace-window-left-title-bar",
    ) as HTMLElement | null;
    const footerEl = el.querySelector(".modal-footer") as HTMLElement | null;
    const bodyEl = el.querySelector(".draggable-body") as HTMLElement | null;
    const headerHeight = headerEl?.getBoundingClientRect().height ?? 0;
    const footerHeight = footerEl?.getBoundingClientRect().height ?? 0;
    // measure actual body padding (top + bottom) to be precise
    let bodyPadding = 0;
    if (bodyEl) {
      const cs = window.getComputedStyle(bodyEl);
      const pt = parseFloat(cs.paddingTop || "0") || 0;
      const pb = parseFloat(cs.paddingBottom || "0") || 0;
      bodyPadding = pt + pb;
    }
    // small safety margin so footer/button don't get clipped by rounding/layout
    const safetyMargin = 12;
    const total = Math.round(
      headerHeight + footerHeight + bodyPadding + safetyMargin,
    );
    el.style.setProperty("--modal-body-offset", `${total}px`);
  }, []);

  // if the window size changes, reposition windows as needed
  useEffect(() => {
    window.addEventListener("resize", handleViewableAreaResize);
    window.addEventListener("resize", setModalBodyOffset);
    // initialize offset on mount as well
    setModalBodyOffset();
    return () => {
      window.removeEventListener("resize", handleViewableAreaResize);
      window.removeEventListener("resize", setModalBodyOffset);
    };
    /**
     * Disabling eslint here because adding [handleZIndexIncrement] will cause the `handleZIndexIncrement`
     * callback to be re-rendered infinitely since the the useEffect would trigger the the useCallback to
     * update and vice versa
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleViewableAreaResize]);

  const hasFocus = zIndex === focusZIndex;

  // repositionWithinBounds moved to `drag-resize.ts`
  // create handlers for drag/resize behavior
  const { handleMouseDown, onResizeClick } = createDragResizeHandlers({
    elementRef,
    mousePos,
    componentPos,
    componentSize,
    selectedHandle,
    dragCallback,
    resizeCallback,
  });

  // on mount normalize initial position and ensure modal doesn't overlap header or extend past bottom
  useEffect(() => {
    if (!elementRef.current) return;
    // normalize numeric initialPosition values to pixel strings if provided
    if (initialPosition?.left && typeof initialPosition.left === "number") {
      elementRef.current.style.left = `${initialPosition.left}px`;
    }
    if (initialPosition?.top && typeof initialPosition.top === "number") {
      elementRef.current.style.top = `${initialPosition.top}px`;
    }
    // If modalOnly is enabled, create an overlay to capture clicks and trap focus
    if (modalOnly) {
      const overlay = document.createElement("div");
      overlay.style.position = "fixed";
      overlay.style.left = "0";
      overlay.style.top = "0";
      overlay.style.width = "100vw";
      overlay.style.height = "100vh";
      overlay.style.background = "rgba(0,0,0,0.4)";
      overlay.style.zIndex = `${zIndex - 1}`;
      overlay.style.pointerEvents = "auto"; // capture clicks
      overlay.addEventListener("click", (e) => e.stopPropagation(), {
        capture: true,
      });
      // Prevent background scrolling while modal is open, but allow the modal
      // body to be scrollable. Save previous body overflow and restore on unmount.
      prevBodyOverflow.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.body.appendChild(overlay);
      overlayRef.current = overlay;
    }

    // if initialSize provided, try to honor it but cap to available viewport height
    const requestedHeight = parseSize(initialSize?.height, "height");
    const requestedWidth = parseSize(initialSize?.width, "width");
    const navBar = document.getElementById("page-header");
    const tOffset = navBar?.getBoundingClientRect().bottom ?? 0;
    const bottomBanner = document.getElementById("page-bottom");
    const bottomBannerTop = bottomBanner?.getBoundingClientRect().top;
    const bottomReserved = bottomBannerTop
      ? window.innerHeight - bottomBannerTop + 30
      : 30;
    const availableHeight = window.innerHeight - tOffset - bottomReserved;
    const availableWidth = window.innerWidth - 60; // keep some margin

    if (requestedHeight && requestedHeight > 0) {
      if (availableHeight > 0 && requestedHeight > availableHeight) {
        elementRef.current.style.height = `${availableHeight}px`;
        setForceBodyScroll(true);
      } else {
        // preserve requested height (use px if numeric)
        elementRef.current.style.height =
          typeof initialSize?.height === "number"
            ? `${initialSize?.height}px`
            : ((initialSize?.height as string) ??
              elementRef.current.style.height);
        setForceBodyScroll(false);
      }
    }

    // apply width if requested, but cap to available width so panel doesn't overflow horizontally
    if (requestedWidth && requestedWidth > 0) {
      if (requestedWidth > availableWidth) {
        elementRef.current.style.width = `${availableWidth}px`;
      } else {
        elementRef.current.style.width =
          typeof initialSize?.width === "number"
            ? `${initialSize?.width}px`
            : ((initialSize?.width as string) ??
              elementRef.current.style.width);
      }
    }

    // If initialPosition.top was numeric, ensure the panel sits below the header
    if (initialPosition?.top && typeof initialPosition.top === "number") {
      const navBarRect = document
        .getElementById("page-header")
        ?.getBoundingClientRect();
      const headerBottom = navBarRect?.bottom ?? 0;
      const currentTop = elementRef.current.getBoundingClientRect().top;
      if (currentTop < headerBottom) {
        elementRef.current.style.transform = "";
        elementRef.current.style.top = `${headerBottom}px`;
      }
    }

    // If an explicit initialPosition was provided, enforce viewport bounds now.
    // If no initialPosition was provided, leave the inline 50%/translate centering
    // untouched so the panel remains centered by default.
    if (initialPosition) {
      repositionWithinBounds(elementRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // cleanup overlay on unmount
  useEffect(() => {
    // The modal's own element, captured at mount so the unmount cleanup removes
    // the CSS var from the SAME node (not whatever elementRef points at later).
    const modalEl = elementRef.current;
    return () => {
      if (overlayRef.current) {
        overlayRef.current.remove();
        overlayRef.current = null;
      }
      // restore body overflow when modal unmounts
      if (prevBodyOverflow.current !== null) {
        document.body.style.overflow = prevBodyOverflow.current;
        prevBodyOverflow.current = null;
      }
      // remove any CSS variable we set on the modal element
      if (modalEl) {
        modalEl.style.removeProperty("--modal-body-offset");
      }
    };
    // mount/unmount only
  }, []);

  // default header is rendered above (defaultHeader)

  /** body will grow to fill space between header and footer */

  const defaultHeader = (
    <Box
      component="div"
      sx={{ flexGrow: 0 }}
      className="workspace-window-left-title-bar"
      style={{ height: 50, minHeight: 50, maxHeight: 50 }}
    >
      <TitleBar onMouseDown={handleMouseDown} onClose={onClose} />
    </Box>
  );

  const defaultFooter = (
    <Box
      component="div"
      sx={{ flexGrow: 0 }}
      className="modal-default-footer"
    />
  );

  const resolveHeader = () => {
    if (typeof header === "string" || React.isValidElement(header)) {
      return (
        <TitleBar
          onMouseDown={handleMouseDown}
          onClose={onClose}
          title={header}
          noAction={hideClose}
        />
      );
    } else {
      return defaultHeader;
    }
  };

  const focusClassName = hasFocus ? "in-focus-shadow" : "";

  return (
    <Paper
      elevation={0}
      className={`_modal.tsx modal-panel modal-visibility ${focusClassName}`}
      id={panelId ?? "modal-panel"}
      style={{
        backgroundColor: backgroundColor ?? "rgba(52, 55, 65, 0.95)",
        position: initialPosition ? "absolute" : "fixed",
        left: initialPosition?.left ?? "50%",
        top: initialPosition?.top ?? "50%",
        transform: `translate(-50%, -50%)`,
        width: initialSize?.width,
        zIndex: zIndex,
        ...style,
      }}
      ref={elementRef}
    >
      <div className="resize-container resize-z-index">
        {resizable && (
          <>
            {/* horizontal/vertical resizing handles */}
            <div
              className="resize-v--n resize-z-index"
              onMouseDown={(e) => onResizeClick(e, "n")}
            />
            <div
              className="resize-v--s resize-z-index"
              onMouseDown={(e) => onResizeClick(e, "s")}
            />
            <div
              className="resize-h--e resize-z-index"
              onMouseDown={(e) => onResizeClick(e, "e")}
            />
            <div
              className="resize-h--w resize-z-index"
              onMouseDown={(e) => onResizeClick(e, "w")}
            />

            {/* corner resizing handles */}
            <div
              className="resize-corner--nw resize-z-index"
              onMouseDown={(e) => onResizeClick(e, "nw")}
            />
            <div
              className="resize-corner--ne resize-z-index"
              onMouseDown={(e) => onResizeClick(e, "ne")}
            />
            <div
              className="resize-corner--sw resize-z-index"
              onMouseDown={(e) => onResizeClick(e, "sw")}
            />
            <div
              className="resize-corner--se resize-z-index"
              onMouseDown={(e) => onResizeClick(e, "se")}
            />
          </>
        )}
        <Box
          component="div"
          sx={{ display: "flex", flexDirection: "column" }}
          className="resize-container-content"
        >
          {/* header */}
          {resolveHeader()}

          {/* body */}
          <Box
            component="div"
            sx={{ flexGrow: 1 }}
            className={`draggable-body${
              scrollContent || forceBodyScroll ? " with-scrolling" : ""
            }${bodyClassNames ? ` ${bodyClassNames.join(" ")}` : ""}`}
            onWheel={(e) => {
              // prevent wheel events from bubbling to document/body so the
              // modal's scrollable body handles scrolling instead of the page
              e.stopPropagation();
            }}
            onWheelCapture={(e) => {
              e.stopPropagation();
            }}
          >
            {children}
          </Box>

          {/* footer */}
          {footer ? (
            <Box className="modal-footer" component="div" sx={{ flexGrow: 0 }}>
              {footer}
            </Box>
          ) : (
            defaultFooter
          )}

          {/* This only renders when the panel is whole */}
          {/* {resizable && <ExpandableIconComponent />} */}
        </Box>
      </div>
    </Paper>
  );
};

Modal.defaultProps = {
  resizable: true,
};

export default Modal;
